import { useState, useEffect, useRef, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Link } from "react-router-dom";
import { Toaster, toast } from "sonner";
import axios from "axios";

// Pages
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import MyTrips from "./pages/MyTrips";
import MyTickets from "./pages/MyTickets";
import MyBookings from "./pages/MyBookings";
import MyBudget from "./pages/MyBudget";
import CalendarPage from "./pages/CalendarPage";
import Personal from "./pages/Personal";
import CountryInfo from "./pages/CountryInfo";
import CountriesPage from "./pages/CountriesPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import AIPlanner from "./pages/AIPlanner";
import Flights from "./pages/Flights";
import Hotels from "./pages/Hotels";
import CarRental from "./pages/CarRental";
import BusTrain from "./pages/BusTrain";
import TravelEsim from "./pages/TravelEsim";
import RouteSights from "./pages/RouteSights";
import SharedRoadtrip from "./pages/SharedRoadtrip";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Admin from "./pages/Admin";
import Journal from "./pages/Journal";
import SharedTrip from "./pages/SharedTrip";
import SessionTracker from "./components/SessionTracker";

// Components
import AuthCallback from "./components/AuthCallback";
import ProtectedRoute from "./components/ProtectedRoute";
import { ErrorBoundary } from "./components/ErrorBoundary";
import BottomNav from "./components/BottomNav";
import { LoadScript } from "@react-google-maps/api";

const GOOGLE_MAPS_LIBRARIES = ["places"];
import TopNav from "./components/TopNav";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = (userData) => {
    setUser(userData);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      // Logout errors are non-critical
    }
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser((prev) => ({ ...prev, ...userData }));
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, updateUser, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// App Router Component
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/shared/:shareId" element={<SharedRoadtrip />} />
      <Route path="/trip/:shareId" element={<SharedTrip />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/trips"
        element={
          <ProtectedRoute>
            <MyTrips />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tickets"
        element={
          <ProtectedRoute>
            <MyTickets />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bookings"
        element={
          <ProtectedRoute>
            <MyBookings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/personal"
        element={
          <ProtectedRoute>
            <Personal />
          </ProtectedRoute>
        }
      />
      <Route
        path="/country/:countryCode"
        element={
          <ProtectedRoute>
            <CountryInfo />
          </ProtectedRoute>
        }
      />
      <Route
        path="/countries"
        element={
          <ProtectedRoute>
            <CountriesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/privacy"
        element={
          <ProtectedRoute>
            <PrivacyPolicy />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-planner"
        element={
          <ProtectedRoute>
            <AIPlanner />
          </ProtectedRoute>
        }
      />
      <Route
        path="/journal"
        element={
          <ProtectedRoute>
            <Journal />
          </ProtectedRoute>
        }
      />
      <Route
        path="/flights"
        element={
          <ProtectedRoute>
            <Flights />
          </ProtectedRoute>
        }
      />
      <Route
        path="/hotels"
        element={
          <ProtectedRoute>
            <Hotels />
          </ProtectedRoute>
        }
      />
      <Route
        path="/car-rental"
        element={
          <ProtectedRoute>
            <CarRental />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bus-train"
        element={
          <ProtectedRoute>
            <BusTrain />
          </ProtectedRoute>
        }
      />
      <Route
        path="/esim"
        element={
          <ProtectedRoute>
            <TravelEsim />
          </ProtectedRoute>
        }
      />
      <Route
        path="/route-sights/:countryCode"
        element={
          <ProtectedRoute>
            <RouteSights />
          </ProtectedRoute>
        }
      />
      <Route
        path="/roadtrip"
        element={
          <ProtectedRoute>
            <RouteSights />
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <CalendarPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/budget"
        element={
          <ProtectedRoute>
            <MyBudget />
          </ProtectedRoute>
        }
      />
      {/* Password Reset Routes (Public) */}
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      
      {/* Admin Route */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <Admin />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

// Main App Layout
function AppLayout() {
  const location = useLocation();
  const { user } = useAuth();
  
  const showNav = user && location.pathname !== "/";

  return (
    <div className="App min-h-screen bg-[#080d19]">
      <ScrollToTop />
      {user && <SessionTracker />}
      {showNav && <TopNav />}
      <main>
        <ErrorBoundary>
          <AppRouter />
        </ErrorBoundary>
      </main>
      {showNav && <BottomNav />}
    </div>
  );
}

export const GoogleMapsContext = createContext(false);

function App() {
  const [mapsLoaded, setMapsLoaded] = useState(false);

  return (
    <BrowserRouter>
      <LoadScript
        googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ""}
        libraries={GOOGLE_MAPS_LIBRARIES}
        onLoad={() => setMapsLoaded(true)}
        onError={() => setMapsLoaded(false)}
      >
        <GoogleMapsContext.Provider value={mapsLoaded}>
          <AuthProvider>
            <Toaster 
              position="top-center" 
              richColors 
              theme="dark"
              toastOptions={{
                style: {
                  background: '#18181b',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: '#f4f4f5',
                },
              }}
            />
            <AppLayout />
          </AuthProvider>
        </GoogleMapsContext.Provider>
      </LoadScript>
    </BrowserRouter>
  );
}

export default App;
